import React from 'react';
import _ from 'lodash';
import Tippy from '../../../components/ReactTippy';

import WorkerInfoApi from '../../../services/workers/workerInfo/WorkerInfoApi';

import { isTargetRoleId } from '../../../utilities/roleUtility';
import { WORKER_TAB, USER_ROLES } from '../../../utilities/constants';

import warningIcon from '../../../images/icon/warning-24px.svg';
import Styles from '../../../styles/componentStyle/worker/workerTab/workerTab.scss';
import SessionStorageService from '../../../services/utilities/SessionStorageService';
import { hasRoanPermission } from '../../../utilities/companyUserPermissionUtil';
import { messageUtility } from '../../../utilities/messageUtility';

/**
 * 作業員タブ
 * 協力会社管理者ユーザーは全て表示できる
 * 職長ユーザーは『基本情報』・『顔写真』タブを表示できる
 * @props {number}roleId:役職ID
 * @props {number}selectTabId:選択中のタブのID
 * @props {function}onClick:作業員のタブをクリックした時の処理
 * @props {boolean}iconTypeInconsistencyStatus: check notmatchingRankNameCode show icon tooltip masterNavi
 * @props {string} inconsistencyStatusText: warning message about inconsistency status
 */
export class WorkerTab extends React.Component {
  constructor(props) {
    super(props);

    if (_.isUndefined(props.roleId)) {
      throw new Error('WorkerTab.jsx constructor props.roleId');
    }

    if (_.isUndefined(props.selectTabId)) {
      throw new Error('WorkerTab.jsx constructor props.selectTabId');
    }

    if (_.isUndefined(props.workerId)) {
      // throw new Error('WorkerTab.jsx constructor props.workerId');
      // console.warn('WorkerTab.jsx constructor props.workerId', props.workerId);
      // 作業員新規作成の場合にタブ表示される場合は
      // props.workerID が undefined になるので注意すること
    }

    if (_.isUndefined(props.companyId)) {
      throw new Error('WorkerTab.jsx constructor props.companyId');
    }

    if (!_.isFunction(this.props.onClick)) {
      throw new Error('WorkerTab.jsx props onClick is not function');
    }

    this.state = {
      companyId: this.props.companyId,
      workerId: this.props.workerId,
      isBasicInfoDetailCompleted: false,
      isInsuranceInfoCompleted: false,
      isJobLicenseCompleted: false,
      isAnamnesisCompleted: false,
      isMedicalExaminationCompleted: false,
      isUrgencyContactCompleted: false,
      isTrainingCommendationCompleted: false,
      useGreenFileFlg: false,
      expirationStatus: {},
    };
  }

  /**
   * 代行登録状態であるかどうか
   * @return {boolean}true：代行登録状態、false：代行登録状態ではない
   * */
  isAgency = () => {
    const ownCompanyId = Number(
      SessionStorageService.getSessionPartnerCompanyIdOfPrimeCompany()
    );
    const selectCompanyId = Number(this.props.companyId);
    return ownCompanyId !== selectCompanyId;
  };

  /**
   * 各労務安全項目の入力状況を取得する
   * @param {number}companyId 会社ID
   * @param {number}workerId 作業員ID
   * @return {object}入力状況
   * */
  getInputStatus = async (companyId, workerId) => {
    const inputStatus = {
      isBasicInfoDetailCompleted: false,
      isInsuranceInfoCompleted: false,
      isJobLicenseCompleted: false,
      isAnamnesisCompleted: false,
      isMedicalExaminationCompleted: false,
      isUrgencyContactCompleted: false,
      isTrainingCommendationCompleted: false,
    };
    const requiredResponseKey = [
      'id',
      'company_id',
      'is_basic_info_detail_completed',
      'is_insurance_info_completed',
      'is_job_license_completed',
      'is_anamnesis_completed',
      'is_medical_examination_completed',
      'is_urgency_contact_completed',
      'is_training_commendation_completed',
    ];

    try {
      const response = await WorkerInfoApi.greenFileItemsInputStatus(
        companyId,
        workerId
      );
      if (
        response.result !== true ||
        !_.has(response, 'data.entities') ||
        !requiredResponseKey.every(item => item in response.data.entities)
      ) {
        console.warn(
          'WorkerTab.jsx componentDidMount greenFileItemsInputStatus'
        );
      } else {
        const status = response.data.entities;
        inputStatus.isBasicInfoDetailCompleted =
          status.is_basic_info_detail_completed;
        inputStatus.isInsuranceInfoCompleted =
          status.is_insurance_info_completed;
        inputStatus.isJobLicenseCompleted = status.is_job_license_completed;

        inputStatus.isAnamnesisCompleted = status.is_anamnesis_completed;
        inputStatus.isMedicalExaminationCompleted =
          status.is_medical_examination_completed;
        inputStatus.isUrgencyContactCompleted =
          status.is_urgency_contact_completed;
        inputStatus.isTrainingCommendationCompleted =
          status.is_training_commendation_completed;
      }
    } catch (error) {
      console.warn(
        'WorkerTab.jsx WorkerInfoApi.greenFileItemsInputStatus else',
        error
      );
    }

    return inputStatus;
  };

  getExpirationStatus = async (companyId, workerId) => {
    await WorkerInfoApi.getExpiration(companyId, workerId).then(response => {
      if (!response.result) {
        return;
      }
      this.setState({
        expirationStatus: response.data.entities.worker.expiration,
      });
    });
  };

  /**
   * 労務安全項目のタブ表示状態を設定する
   *
   * @params {number?}workerId: 作業員ID（新規作成時にはundefinedとなる）
   * */
  setRoanTabs = async workerId => {
    let setStateValue = {};
    // 労務安全項目タブ自体を表示するかどうか
    if (this.isAgency()) {
      // 代行登録の場合には制御なしで表示
      setStateValue.useGreenFileFlg = true;
    } else {
      // 代行登録ではない場合はユーザーのサービス権限から取得
      setStateValue.useGreenFileFlg = await hasRoanPermission(
        this.props.companyId
      );
    }

    if (!_.isUndefined(workerId) && setStateValue.useGreenFileFlg) {
      // workerIdがあり（更新の場合）、労務安全が有効なユーザーの場合、労務安全の入力状況を取得する
      const inputStatus = await this.getInputStatus(
        this.props.companyId,
        this.props.workerId
      );
      setStateValue = Object.assign(setStateValue, inputStatus);
    }

    this.setState({
      ...this.state,
      ...setStateValue,
    });
  };

  componentDidMount() {
    this.setRoanTabs(this.props.workerId);
    if (
      !_.isUndefined(this.props.companyId) &&
      !_.isUndefined(this.props.workerId)
    ) {
      this.getExpirationStatus(this.props.companyId, this.props.workerId);
    }
  }

  // 画面表示
  render() {
    return (
      <ul className={`${Styles.workerTab} contruction-navi-list`}>
        {_.map(WORKER_TAB, item => {
          if (
            item.SHOW_PARTNER_ONLY === false ||
            (item.SHOW_PARTNER_ONLY === true &&
              isTargetRoleId([USER_ROLES.partner], this.props.roleId) &&
              this.state.useGreenFileFlg)
          ) {
            const isDisabled =
              _.isUndefined(this.props.workerId) &&
              item.ID !== WORKER_TAB.WORKER_INFO.ID;

            // 各Workersタブに警告アイコン・テキストを設定する
            let warnings = [];
            const setWarning = (id, color, text) => {
              warnings.push({ id, color, text });
            };
            switch (item.NAME) {
              case WORKER_TAB.WORKER_INFO.NAME:
                if (this.props.iconTypeInconsistencyStatus) {
                  setWarning(
                    'WorkerInfo_warning',
                    this.props.iconTypeInconsistencyStatus,
                    this.props.inconsistencyStatusText
                  );
                }
                break;
              case WORKER_TAB.WORKER_SAFETY_BASIC_INFO.NAME:
                if (
                  !_.isUndefined(this.props.workerId) &&
                  this.state.isBasicInfoDetailCompleted === false
                ) {
                  setWarning(
                    'isBasicInfoDetailCompleted',
                    'caution',
                    '労務安全書類に出力される項目が未入力です。'
                  );
                }
                if (this.state.expirationStatus.has_expired_details) {
                  setWarning(
                    'WorkerSafetyBasicInfo_warning',
                    'warning',
                    messageUtility.getMessage('E-001-105')
                  );
                }
                if (this.state.expirationStatus.has_about_to_expire_details) {
                  setWarning(
                    'WorkerSafetyBasicInfo_caution',
                    'caution',
                    messageUtility.getMessage('E-001-106')
                  );
                }
                if (
                  this.state.expirationStatus.has_foreign_construction_worker
                ) {
                  setWarning(
                    'WorkerSafetyBasicInfo_warning',
                    'warning',
                    messageUtility.getMessage('E-001-107')
                  );
                }
                break;
              case WORKER_TAB.JOB_LICENSE.NAME:
                if (
                  !_.isUndefined(this.props.workerId) &&
                  this.state.isJobLicenseCompleted === false
                ) {
                  setWarning(
                    'isJobLicenseCompleted',
                    'caution',
                    '労務安全書類に出力される項目が未入力です。'
                  );
                }
                if (
                  _.some(
                    [
                      'has_expired_core_technicians',
                      'has_expired_engineer_licenses',
                      'has_expired_licenses',
                    ],
                    warning => {
                      return this.state.expirationStatus[warning];
                    }
                  )
                ) {
                  setWarning(
                    'JobLicense_warning',
                    'warning',
                    messageUtility.getMessage('E-001-105')
                  );
                }
                if (
                  _.some(
                    [
                      'has_about_to_expire_core_technicians',
                      'has_about_to_expire_engineer_licenses',
                      'has_about_to_expire_licenses',
                    ],
                    warning => {
                      return this.state.expirationStatus[warning];
                    }
                  )
                ) {
                  setWarning(
                    'JobLicense_caution',
                    'caution',
                    messageUtility.getMessage('E-001-106')
                  );
                }
                break;
              case WORKER_TAB.INSURANCE_INFO.NAME:
                if (
                  !_.isUndefined(this.props.workerId) &&
                  this.state.isInsuranceInfoCompleted === false
                ) {
                  setWarning(
                    'isInsuranceInfoCompleted',
                    'caution',
                    '労務安全書類に出力される項目が未入力です。'
                  );
                }
                if (
                  _.some(
                    [
                      'has_expired_health_insurances',
                      'has_expired_pension_insurances',
                    ],
                    warning => {
                      return this.state.expirationStatus[warning];
                    }
                  )
                ) {
                  setWarning(
                    'InsuranceInfo_warning',
                    'warning',
                    messageUtility.getMessage('E-001-105')
                  );
                }
                if (
                  _.some(
                    [
                      'has_about_to_expire_health_insurances',
                      'has_about_to_expire_pension_insurances',
                    ],
                    warning => {
                      return this.state.expirationStatus[warning];
                    }
                  )
                ) {
                  setWarning(
                    'InsuranceInfo_caution',
                    'caution',
                    messageUtility.getMessage('E-001-106')
                  );
                }
                break;
              case WORKER_TAB.ANAMNESES.NAME:
                if (
                  !_.isUndefined(this.props.workerId) &&
                  this.state.isAnamnesisCompleted === false
                ) {
                  setWarning(
                    'isAnamnesisCompleted',
                    'caution',
                    '労務安全書類に出力される項目が未入力です。'
                  );
                }
                break;
              case WORKER_TAB.MEDICAL_EXAMINATION.NAME:
                if (
                  !_.isUndefined(this.props.workerId) &&
                  this.state.isMedicalExaminationCompleted === false
                ) {
                  setWarning(
                    'isMedicalExaminationCompleted',
                    'caution',
                    '労務安全書類に出力される項目が未入力です。'
                  );
                }
                if (
                  _.some(
                    [
                      'has_expired_medical_examinations',
                      'has_expired_special_medical_examination',
                    ],
                    warning => {
                      return this.state.expirationStatus[warning];
                    }
                  )
                ) {
                  setWarning(
                    'MedicalExamination_warning',
                    'warning',
                    messageUtility.getMessage('E-001-105')
                  );
                }
                if (
                  _.some(
                    [
                      'has_about_to_expire_medical_examinations',
                      'has_about_to_expire_special_medical_examination',
                    ],
                    warning => {
                      return this.state.expirationStatus[warning];
                    }
                  )
                ) {
                  setWarning(
                    'MedicalExamination_caution',
                    'caution',
                    messageUtility.getMessage('E-001-106')
                  );
                }
                break;
              case WORKER_TAB.URGENCY_CONTACT.NAME:
                if (
                  !_.isUndefined(this.props.workerId) &&
                  this.state.isUrgencyContactCompleted === false
                ) {
                  setWarning(
                    'isUrgencyContactCompleted',
                    'caution',
                    '労務安全書類に出力される項目が未入力です。'
                  );
                }

                break;
              case WORKER_TAB.TRAINING_COMMENDATION.NAME:
                if (
                  !_.isUndefined(this.props.workerId) &&
                  this.state.isTrainingCommendationCompleted === false
                ) {
                  setWarning(
                    'isTrainingCommendationCompleted',
                    'caution',
                    '労務安全書類に出力される項目が未入力です。'
                  );
                }
                if (this.state.expirationStatus.has_expired_safety_trainings) {
                  setWarning(
                    'TrainingCommendation_warning',
                    'warning',
                    messageUtility.getMessage('E-001-105')
                  );
                }
                if (
                  this.state.expirationStatus
                    .has_about_to_expire_safety_trainings
                ) {
                  setWarning(
                    'TrainingCommendation_caution',
                    'caution',
                    messageUtility.getMessage('E-001-106')
                  );
                }
                break;
              default:
                break;
            }
            return (
              <li
                className={`contruction-navi-list__item ${isDisabled &&
                  'disabled'}`}
                key={item.ID}
              >
                <Tippy
                  duration={10}
                  distance={24}
                  arrow='true'
                  arrowType='sharp'
                  delay={[50, 100]}
                  placement='bottom'
                  followCursor={true}
                  html={
                    <ul className='darklist__tooltip'>
                      {_.map(warnings, item => (
                        <li style={{ color: '#ffffff' }} key={item.id}>
                          ・{item.text}
                        </li>
                      ))}
                    </ul>
                  }
                  disable={_.isEmpty(warnings)}
                >
                  <a
                    key={`${String(item.ID)}_a`}
                    onClick={event => this.props.onClick(event, item.ID)}
                    className={
                      this.props.selectTabId === item.ID
                        ? 'line-list line-list--contruction-navi-list select'
                        : `line-list line-list--contruction-navi-list ${isDisabled &&
                            'disabled'}`
                    }
                  >
                    <div className='line-list--contruction-navi-value'>
                      <span className='tab-label'>{`${item.NAME}`}</span>
                      {!_.isEmpty(warnings) && (
                        <WorkerTabWarningIcon
                          color={
                            _.some(warnings, warning => {
                              return warning.color === 'warning';
                            })
                              ? 'warning'
                              : 'caution'
                          }
                        />
                      )}
                    </div>
                  </a>
                </Tippy>
              </li>
            );
          }
        })}
      </ul>
    );
  }
}

const WorkerTabWarningIcon = props => {
  const { color = '' } = props;
  return (
    <div
      className={`line-list__warning-icon line-list__warning-icon--${color}`}
    >
      <i className='icon-warning_round' />
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/worker/tab/WorkerTab.jsx